import ApiService from "@services/apiService";
import {generalMeetings, execMeetings, defaultRoles} from "@/views/club-application/utils/default-values";

const ClubApplicationService = {
    transformToApiData: (inputData) => {
        const createRoleAttributes = (role) => {
            if (role.roleType === "executive_role" || role.roleType === "support_role") {
                return {
                    role_name: role.roleName,
                    role_key: role.roleId,
                    role_type: role.roleType,
                    signing_authority: role.removable !== undefined ? !role.removable : false,
                    responsibilities: role.responsibilities || []
                }
            }
        };

        const createSigningAuthority = (role, signingAuths) => {
            if (role !== undefined) {
                if (signingAuths.includes(role.role_key)) {
                    return {...role, signing_authority: true};
                } else {
                    return {...role, signing_authority: false};
                }
            }
        };

        const formClubInfo = inputData.formClubInfo;
        const formClubOperation = inputData.formClubOperation;
        const formMembership = inputData.formMembership;
        const formMemberList = inputData.formMemberList;

        const transformedData = {
            club_application: {
                draft_id: inputData.draftId || null,
                club_name: formClubInfo.clubName || "",
                club_acronym: formClubInfo.clubAcronym || null,
                tagline: formClubInfo.clubTagline || "",
                description: formClubInfo.clubDescription || "",
                cover_letter: formClubInfo.clubCoverLetter || "",
                purpose: formClubInfo.clubPurpose || "",
                activities: formClubInfo.clubActivities || "",
                general_meeting_frequency: formClubOperation.generalMeetingFrequency || "",
                executive_meeting_frequency: formClubOperation.executiveMeetingFrequency || "",
                important_meeting_contact_method: formClubOperation.importantMeetingContactMethod || "",
                emergency_meeting_contact_method: formClubOperation.emergencyMeetingContactMethod || "",
                other_constitution_text: formClubOperation.otherSections || "",
                membership_fee: formMembership.clubMemberFee || "",
                category_ids: formClubInfo.clubCategory
                    ? formClubInfo.clubCategory.map(category => category.id)
                    : [],
                executive_roles_attributes: formMembership.clubRoles.map(createRoleAttributes).map(role => createSigningAuthority(role, formMembership.clubSigningAuthorities)),
                internal_affiliations_attributes: formClubOperation.affiliations
                    .filter(affiliation => affiliation.affiliationType === "Internal")
                    .map(affiliation => ({
                        affiliation_name: affiliation.affiliationName,
                        description: affiliation.affiliationConditions
                    })),
                external_affiliations_attributes: formClubOperation.affiliations
                    .filter(affiliation => affiliation.affiliationType === "External")
                    .map(affiliation => {
                        const attrs = {
                            affiliation_name: affiliation.affiliationName,
                            description: affiliation.affiliationConditions,
                        };
                        if (affiliation.uploadedBlobId) {
                            attrs.cover_letter = affiliation.uploadedBlobId;
                        }
                        return attrs;
                    }),
                ...(formMembership.clubMemberFee === "has_fee" && {
                    member_fee_attributes: {
                        fee_amount: formMembership.clubFeeAmount || 0,
                        fee_description: formMembership.clubFeeUse || ""
                    }
                }),
                members_attributes: formMemberList.assignedMembers.map(member => {
                    const memberAttributes = {
                        user_id: member.userObject.id
                    };
                    if (member.roleId !== "general_member") {
                        memberAttributes.role_key = member.roleId;
                    }
                    return memberAttributes;
                })
            }
        };

        // console.log(transformedData);
        return transformedData;
    },
    transformFromDraftApiData: (raw) => {
        const {draft} = raw;

        const createRole = (role) => ({
            roleName: role.role_name,
            roleId: role.role_key,
            roleType: role.role_type,
            responsibilities: role.responsibilities || [],
            removable: defaultRoles.find(defaultRole => defaultRole.roleId === role.role_key)?.removable,
        });

        const generalMeetingFrequency = generalMeetings.find(meeting => meeting.name === draft.general_meeting_frequency);
        const executiveMeetingFrequency = execMeetings.find(meeting => meeting.name === draft.executive_meeting_frequency);
        return {
            draftId: raw.draft_id,
            formClubInfo: {
                clubName: draft.club_name || "",
                clubAcronym: draft.club_acronym || "",
                clubTagline: draft.tagline || "",
                clubDescription: draft.description || "",
                clubCoverLetter: draft.cover_letter || "",
                clubPurpose: draft.purpose || "",
                clubActivities: draft.activities || "",
                clubCategory: draft.categories
                    ? draft.categories
                    : [],
            },
            formClubOperation: {
                generalMeetingFrequency: generalMeetingFrequency?.value || "",
                executiveMeetingFrequency: executiveMeetingFrequency?.value || "",
                importantMeetingContactMethod: draft.important_meeting_contact_method || "",
                emergencyMeetingContactMethod: draft.emergency_meeting_contact_method || "",
                otherSections: draft.other_constitution_text || "",
                affiliations: [
                    ...(draft.internal_affiliations || []).map(affiliation => ({
                        affiliationType: "Internal",
                        affiliationName: affiliation.affiliation_name,
                        affiliationConditions: affiliation.description,
                        files: [],
                    })),
                    ...(draft.external_affiliations || []).map(affiliation => ({
                        affiliationType: "External",
                        affiliationName: affiliation.affiliation_name,
                        affiliationConditions: affiliation.description,
                        uploadedBlobId: affiliation.cover_letter,
                        files: [],
                    })),
                ],
            },
            formMembership: {
                clubRoles: draft.executive_roles.map(createRole).concat({
                    roleName: "General Member",
                    roleType: "general_member",
                    roleId: "general_member",
                    moreThanOne: true,
                }),
                clubSigningAuthorities: draft.executive_roles
                    .filter(role => role.signing_authority)
                    .map(role => role.role_key),
                clubMemberFee: draft.membership_fee || "membership_free",
                ...(draft.member_fee && {
                    clubFeeAmount: parseFloat(draft.member_fee.fee_amount) || 0,
                    clubFeeUse: draft.member_fee.fee_description || "",
                }),
            },
            formMemberList: {
                assignedMembers: draft.members.map(member => ({
                    userObject: {
                        id: member.user.id,
                        username: member.user.username,
                        first_name: member.user.first_name,
                        last_name: member.user.last_name,
                    },
                    roleId: member.role_key || "general_member",
                })),
            },
        };
    },
    createClub: async (club) => {
        const response = await ApiService.post('/club_applications', club);
        return response.data;
    },
    getClubName: async (clubName) => {
        const response = await ApiService.get(`/clubs/name_exists?name=${clubName}`);
        return response.data;
    },
    saveDraft: async (data) => {
        const response = await ApiService.post('/club_applications/save_draft', data);
        return response.data;
    },
    restoreDraft: async (id) => {
        const response = await ApiService.get(`/club_applications/restore_draft/${id}`);
        return response.data;

    },
    getCategoryList: async () => {
        const response = await ApiService.get(`/categories`);
        return response.data;
    },
    getClubApplication: async (id) => {
        const response = await ApiService.get(`/club_applications/${id}`);
        return response.data;
    },
    submitForApproval: async (id, params) => {
        const response = await ApiService.post(`/club_applications/${id}/submit`, params);
        return response.data;
    },
    withdraw: async (id) => {
        const response = await ApiService.post(`/club_applications/${id}/withdraw`);
        return response.data;
    },
    reopen: async (id) => {
        const response = await ApiService.post(`/club_applications/${id}/reopen`);
        return response.data;
    },
    getApprovers: async (id) => {
        const response = await ApiService.get(`/club_applications/${id}/eligible_approvers/approve`);
        return response.data;
    },
    update: async (id, params) => {
        const response = await ApiService.patch(`/club_applications/${id}`, params);
        return response.data;
    },
    submitPublicComment: async (form_id, clubapp_id, params) => {
        const response = await ApiService.post(`/forms/${form_id}/comments/${clubapp_id}/create_public`, params);
        return response.data;
    },
    getRequestHistory: async (form_id, clubapp_id) => {
        try {
            const response = await ApiService.get(`/forms/${form_id}/request_histories/${clubapp_id}`);
            return response.data;
        } catch (error) {
            return error.response.data;
        }
    },

    getAvailability: async () => {
        const response = await ApiService.get('/club_applications/availability');
        return response.data;
    }

};


export default ClubApplicationService;



